"use client";

import { useEffect, useRef } from "react";
import type { KeyboardEvent } from "react";
import { joinFrench } from "@/lib/analytics";
import styles from "./ConsentBanner.module.css";
import { allPurposes, CONSENT_VALIDITY_MONTHS } from "./consent";
import type { ConsentChoices } from "./consent";

/** Ancre de la section cookies de la politique de confidentialité. */
export const COOKIES_POLICY_HREF = "/confidentialite/#cookies";

type ConsentBannerProps = {
  /** Outils de mesure nommés dans le texte (« Google Analytics », « PostHog »). */
  tools: ReadonlyArray<string>;
  onDecide: (choices: ConsentChoices) => void;
  /** Rouvert par le visiteur (lien « Cookies ») : le focus entre dans le bandeau. */
  focusOnOpen?: boolean;
  /** Échap ferme sans rien changer, seulement si un choix existe déjà. */
  onDismiss?: () => void;
};

/**
 * Bandeau cookies (règles CNIL) : la finalité et les outils sont nommés,
 * « Refuser » et « Accepter » sont deux boutons de même poids, la durée du
 * choix et le moyen d'en changer sont dits, la politique est à un clic.
 *
 * Une seule finalité aujourd'hui (mesure d'audience) : pas d'écran
 * « Personnaliser ». Les boutons décident pour toutes les finalités de
 * `CONSENT_PURPOSES` ; avec une seconde finalité (publicité), ajouter ici un
 * écran qui les liste une à une.
 *
 * Accessibilité : boîte de dialogue non modale, nommée par son titre et
 * décrite par son texte. Elle est rendue en tête de page (avant le menu) :
 * au clavier, elle vient en premier, sans traverser toute la page.
 */
export function ConsentBanner({ tools, onDecide, focusOnOpen = false, onDismiss }: ConsentBannerProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusOnOpen) ref.current?.focus();
  }, [focusOnOpen]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape" && onDismiss) {
      event.stopPropagation();
      onDismiss();
    }
  };

  return (
    <div
      ref={ref}
      className={styles.banner}
      role="dialog"
      aria-labelledby="consent-title"
      aria-describedby="consent-text"
      tabIndex={-1}
      onKeyDown={onKeyDown}
      data-consent-banner="true"
    >
      <p id="consent-title" className={styles.title}>
        Mesure d&apos;audience
      </p>
      <p id="consent-text" className={styles.text}>
        Avec votre accord, nous mesurons la fréquentation du site avec {joinFrench(tools)}, pour savoir quelles pages vous sont utiles et
        les améliorer. Si vous refusez, aucun cookie de mesure n&apos;est déposé. Votre choix est gardé {CONSENT_VALIDITY_MONTHS} mois, et
        vous pouvez le changer à tout moment avec le lien « Cookies » en bas de page.{" "}
        <a className={styles.link} href={COOKIES_POLICY_HREF}>
          En savoir plus
        </a>
      </p>
      <div className={styles.actions}>
        <button type="button" className={styles.button} onClick={() => onDecide(allPurposes(false))}>
          Refuser
        </button>
        <button type="button" className={styles.button} onClick={() => onDecide(allPurposes(true))}>
          Accepter
        </button>
      </div>
    </div>
  );
}
